import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [scrollY, setScrollY] = useState(0);
  const [showHeroWhatsAppMenu, setShowHeroWhatsAppMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d3taiq3onp7ib57t363g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  const whatsappNumbers = [
    { state: 'Distrito Federal', number: '5561998017669' },
    { state: 'São Paulo', number: '5511920545233' },
    { state: 'Minas Gerais', number: '5531996645637' },
    { state: 'Mato Grosso do Sul', number: '5567998010523' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#4A90A4]/10 to-white">
        <div 
          className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/a0fff0b1bcf73b6866597e05d096d3ef.jpg')] bg-cover bg-center opacity-20"
          style={{
            transform: `translateY(${scrollY * 0.5}px)`,
          }}
        ></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <img
              src="https://static.readdy.ai/image/c2ffb6ae5c67efb323410a310538f1c7/b294b7a2202c93a02cae1a0f9146a692.png"
              alt="InbraPrev"
              className="h-24 mb-8"
            />
            <h1 className="text-6xl font-bold text-[#2C5F2D] mb-6">
              Seu Benefício foi indeferido?
            </h1>
            <h2 className="text-4xl font-semibold text-[#4A90A4] mb-8">
              Fique calmo, nós podemos te ajudar. Nem sempre ter o benefício negado significa que você não tem direito a ele.
            </h2>
            <p className="text-xl text-gray-700 mb-12 max-w-2xl">
              Fale conosco, possuímos uma equipe técnica qualificada para te auxiliar em todos os processos da previdência social.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contato"
                className="px-8 py-4 bg-[#2C5F2D] text-white rounded-lg font-semibold hover:bg-[#4A90A4] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Começar Agora
              </a>
              
              {/* WhatsApp Button with Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowHeroWhatsAppMenu(!showHeroWhatsAppMenu)}
                  className="px-8 py-4 bg-[#25D366] text-white rounded-lg font-semibold hover:bg-[#128C7E] transition-all duration-300 flex items-center gap-3 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-2xl"></i>
                  WhatsApp
                </button>

                {/* Dropdown Menu */}
                {showHeroWhatsAppMenu && (
                  <>
                    {/* Overlay to close menu when clicking outside */}
                    <div 
                      className="fixed inset-0 z-[9998]"
                      onClick={() => setShowHeroWhatsAppMenu(false)}
                    ></div>
                    
                    <div className="absolute top-0 left-full ml-2 bg-white rounded-lg shadow-2xl border border-gray-200 py-3 px-3 z-[9999] whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-700 mb-3 px-2">
                        Escolha sua unidade:
                      </div>
                      <div className="flex flex-row gap-2">
                        {whatsappNumbers.map((item) => (
                          <a
                            key={item.state}
                            href={`https://wa.me/${item.number}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex flex-col items-center gap-2 px-3 py-2 hover:bg-[#25D366]/10 transition-colors cursor-pointer rounded-lg"
                            onClick={() => setShowHeroWhatsAppMenu(false)}
                          >
                            <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                              <i className="ri-whatsapp-fill text-white text-lg"></i>
                            </div>
                            <span className="text-gray-700 font-medium text-xs text-center whitespace-nowrap">{item.state}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#4A90A4]/5 to-white p-8 rounded-2xl border border-[#4A90A4]/20 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#4A90A4] rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2C5F2D] mb-4">Time Especializado</h3>
              <p className="text-gray-600">Profissionais qualificados</p>
            </div>

            <div className="bg-gradient-to-br from-[#4A90A4]/5 to-white p-8 rounded-2xl border border-[#4A90A4]/20 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#2C5F2D] rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2C5F2D] mb-4">Foco no Cliente</h3>
              <p className="text-gray-600">Resolver seus problemas antes que eles virem uma dor de cabeça</p>
            </div>

            <div className="bg-gradient-to-br from-[#4A90A4]/5 to-white p-8 rounded-2xl border border-[#4A90A4]/20 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-[#4A90A4] rounded-full flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#2C5F2D] mb-4">Suporte Diferenciado</h3>
              <p className="text-gray-600">Sempre pronto para apoiar</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicos" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h5 className="text-[#4A90A4] font-semibold text-lg mb-4 uppercase tracking-wide">Nossos Serviços</h5>
              <h2 className="text-5xl font-bold text-[#2C5F2D] mb-6">
                Serviços disponíveis para você
              </h2>
              <p className="text-gray-600 text-lg mb-8">
                Se for para ter um processo, que esta seja a última solução. A atuação da InbraPrev é focada para resolver futuros problemas antes que eles aconteçam.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Aposentadoria Especial</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Aposentadoria por Tempo de Contribuição</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Benefício de Incapacidade parcial ou permanente (Auxílio Doença)</span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Revisões</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">LOAS - Lei Orgânica da Assistência Social</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Aposentadoria por Idade</span>
                  </div>
                </div>
              </div>

              <a
                href="#contato"
                className="inline-block px-8 py-4 bg-[#2C5F2D] text-white rounded-lg font-semibold hover:bg-[#4A90A4] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Fale Conosco
              </a>
            </div>

            <div className="grid grid-cols-1 gap-6">
              <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
                <img
                  src="https://readdy.ai/api/search-image?query=Happy%20elderly%20Brazilian%20couple%20smiling%20together%20celebrating%20retirement%20benefits%20approval%20warm%20natural%20lighting%20simple%20clean%20white%20background%20professional%20photography%20joyful%20atmosphere&width=800&height=600&seq=service1&orientation=landscape"
                  alt="Serviços de Aposentadoria"
                  className="w-full h-80 object-cover object-top"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300">
                <img
                  src="https://static.readdy.ai/image/c2ffb6ae5c67efb323410a310538f1c7/be4fed27d97b1291178c48b4f454845a.png"
                  alt="Consultoria Previdenciária"
                  className="w-full h-80 object-cover object-top"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#2C5F2D] to-[#4A90A4]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-team-line text-4xl text-white"></i>
              </div>
              <div className="text-5xl font-bold text-white mb-2">2.917+</div>
              <div className="text-white/90 text-lg">Clientes Felizes</div>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-file-list-3-line text-4xl text-white"></i>
              </div>
              <div className="text-5xl font-bold text-white mb-2">1.833+</div>
              <div className="text-white/90 text-lg">Processos Concluídos</div>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-line text-4xl text-white"></i>
              </div>
              <div className="text-5xl font-bold text-white mb-2">8+</div>
              <div className="text-white/90 text-lg">Anos de Experiência</div>
            </div>
          </div>
        </div>
      </section>

      {/* About & Testimonials Section */}
      <section id="quemsomos" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="bg-gradient-to-br from-[#4A90A4]/5 to-white p-12 rounded-2xl border border-[#4A90A4]/20">
              <h5 className="text-[#4A90A4] font-semibold text-lg mb-4 uppercase tracking-wide">Quem Somos Nós</h5>
              <h2 className="text-4xl font-bold text-[#2C5F2D] mb-6">
                Seja bem vindo à InbraPrev
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                Nosso escritório é pensado e estruturado para atendimento personalizado, com resposta qualificada e individualizada nas questões submetidas por nossos clientes. Estamos comprometidos com a qualidade bem como com a relação de confiança e transparência em nossos contatos.
              </p>
              <a
                href="#contato"
                className="inline-block px-8 py-4 bg-[#2C5F2D] text-white rounded-lg font-semibold hover:bg-[#4A90A4] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Fale Conosco
              </a>
            </div>

            <div id="depoimentos" className="bg-gradient-to-br from-[#2C5F2D] to-[#4A90A4] p-12 rounded-2xl shadow-2xl">
              <div className="text-center mb-8">
                <i className="ri-double-quotes-l text-6xl text-white/30"></i>
              </div>
              <p className="text-white text-lg mb-8 leading-relaxed">
                Necessitei de ajuda de advogados e levei a questão para a equipe, que conseguiu resolver, atendendo minha expectativa. Apesar da demora do Judiciário, o resultado foi melhor do que eu esperava.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden border-4 border-white/30">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20happy%20retired%20Brazilian%20man%20smiling%20confident%20expression%20simple%20clean%20background%20warm%20natural%20lighting%20headshot%20photography&width=200&height=200&seq=testimonial1&orientation=squarish"
                    alt="Luciano Ferry"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <div className="text-white font-bold text-xl">Luciano Ferry</div>
                  <div className="text-white/80">Aposentado</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Depoimentos Header Section */}
      <section className="py-16 bg-gradient-to-br from-[#2C5F2D] to-[#4A90A4]">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-4">Depoimentos</h2>
          <p className="text-white/90 text-xl">Histórias de clientes satisfeitos</p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="text-5xl font-bold text-[#2C5F2D] mb-8">
                Não perca tempo!
              </h2>

              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Preencha o formulário ao lado e nossa equipe especializada entrará em contato com você o mais breve possível.
              </p>

              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Cada caso é único e merece atenção personalizada. Conte-nos sua situação e vamos encontrar juntos a melhor solução para garantir seus direitos previdenciários.
              </p>

              <div className="bg-gradient-to-br from-[#4A90A4]/10 to-white p-8 rounded-2xl border border-[#4A90A4]/30">
                <h4 className="text-2xl font-bold text-[#2C5F2D] mb-4">Por que nos escolher?</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Atendimento personalizado e humanizado</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Equipe técnica qualificada</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Transparência em todas as etapas</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-[#4A90A4] text-xl mt-1"></i>
                    <span className="text-gray-700">Mais de 8 anos de experiência</span>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h6 className="text-[#4A90A4] font-semibold text-xl mb-4">Horário de Funcionamento</h6>
                <div className="flex items-center gap-4 text-gray-700 mb-6">
                  <span className="font-semibold">Segunda - Sexta</span>
                  <div className="flex items-center gap-2">
                    <i className="ri-time-line text-[#4A90A4]"></i>
                    <span>09:00 AM - 17:00 PM</span>
                  </div>
                </div>

                <h6 className="text-[#4A90A4] font-semibold text-xl mb-4">Clique para ser atendido pela unidade mais próxima</h6>
                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="https://wa.me/5561998017669"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#25D366]/10 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-fill text-white text-xl"></i>
                    </div>
                    <span className="font-semibold text-gray-700">Distrito Federal</span>
                  </a>

                  <a
                    href="https://wa.me/5511920545233"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#25D366]/10 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-fill text-white text-xl"></i>
                    </div>
                    <span className="font-semibold text-gray-700">São Paulo</span>
                  </a>

                  <a
                    href="https://wa.me/5531996645637"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#25D366]/10 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-fill text-white text-xl"></i>
                    </div>
                    <span className="font-semibold text-gray-700">Minas Gerais</span>
                  </a>

                  <a
                    href="https://wa.me/5567998010523"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-[#25D366]/10 rounded-lg hover:bg-[#25D366]/20 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-10 h-10 bg-[#25D366] rounded-full flex items-center justify-center">
                      <i className="ri-whatsapp-fill text-white text-xl"></i>
                    </div>
                    <span className="font-semibold text-gray-700">Mato Grosso do Sul</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-xl border border-[#4A90A4]/20">
              <h3 className="text-3xl font-bold text-[#2C5F2D] mb-6">Fale Conosco</h3>
              <p className="text-gray-600 mb-6">Preencha o formulário abaixo e entraremos em contato</p>

              <form className="space-y-6" id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                    Nome
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                    Telefone (WhatsApp)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="benefit" className="block text-gray-700 font-semibold mb-2">
                    Qual benefício você deseja requerer?
                  </label>
                  <input
                    type="text"
                    id="benefit"
                    name="benefit"
                    required
                    placeholder="Ex: Aposentadoria por Idade, Auxílio Doença, etc."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90A4] text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                    Mensagem
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A90A4] resize-none text-sm"
                  ></textarea>
                  <p className="text-sm text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                {formStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    Erro ao enviar mensagem. Por favor, tente novamente.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full px-8 py-4 bg-[#2C5F2D] text-white rounded-lg font-semibold hover:bg-[#4A90A4] transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {formStatus === 'sending' ? 'Enviando...' : 'Enviar'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#2C5F2D] text-white py-8">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/80">InbraPrev - Assessoria Previdenciária</p>
            <p className="text-white/80">Todos os direitos reservados</p>
            <a
              href="https://readdy.ai/?origin=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
